import React, { useEffect, useState, useRef } from "react";
import fetch from "node-fetch";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import ProgressBar from "react-bootstrap/ProgressBar";
import { useParams } from "react-router-dom";
import "./index.css";
import { API_URL, CONF, SOCKET_URL } from "../config";
import ReCAPTCHA from "react-google-recaptcha";
import { io } from "socket.io-client";
import { LanguageContext } from "../i18n/context";

const initialFormState = {
  posterId: "",
  title: "",
  authors: "",
  topic: "",
  session: "",
  file: "",
  fileName: "",
  recordingFile: "",
  recordingFileName: "",

  authorFullName: "",
  authorEmail: "",
  authorFacebook: "",
  authorSkype: "",
  authorLn: "",
  authorTwitter: "",

  fullName: "",
  email: "",
  phone: "",

  gdprConsent: false,
  postersConsent: false,
  posterIdInvalid: false,
  posterIdValidationMessage: "",
  fileValid: false,
  validationMessage: "",
  recordingFileInvalid: false,
  recordingFileValidationMessage: "",

  failure: false,
  isSending: false,
  sent: false,
};
let client = null;
const NewApplication = () => {
  const { posterId } = useParams();
  const recaptchaRef = useRef(null);
  const [formState, setFormState] = useState(initialFormState);
  const [formValidationMessage, setFormValidationMessage] =
    useState(`Błąd wysyłania formularza. Upewnij się, że wprowadzone dane są
  prawidłowe i spróbuj jeszcze raz. Gdy błąd będzie się powtarzał,
  skontaktuj sie z Biurem Organizacyjnym.`);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    client = io(SOCKET_URL);

    client.on("connect", () => console.log("connected"));

    client.on("progress", (step) => {
      switch (step) {
        case "checkCaptcha": {
          setProgress(10);
          break;
        }
        case "loadConfig": {
          setProgress(15);
          break;
        }
        case "loadPostersData": {
          setProgress(20);
          break;
        }
        case "convertPresentationFile": {
          setProgress(50);
          break;
        }
        case "saveRecordings": {
          setProgress(55);
          break;
        }
        case "uploadFilesToFtp": {
          setProgress(70);
          break;
        }
        case "updateSpreadsheetSlides": {
          setProgress(85);
          break;
        }
        case "done": {
          setProgress(100);
          break;
        }

        default:
          setProgress((s) => s);
          break;
      }
      console.log(step);
    });

    client.connect();
    return () => {
      client.off("progress");
      client.disconnect();
    };
  }, []);

  useEffect(() => {
    const getApplicationDetails = async () => {
      try {
        const result = await fetch(
          `${API_URL}/new-application/${posterId}?conf=${CONF}`
        );
        const details = await result.json();
        setFormState({
          ...initialFormState,
          ...details,
        });
      } catch (e) {
        console.log(e);
      }
    };

    posterId && getApplicationDetails();
  }, [posterId]);

  const handleSubmit = async (event) => {
    event.persist();
    event.preventDefault();
    event.stopPropagation();

    const data = new FormData();

    const {
      title,
      authors,
      file,
      fileName,
      recordingFile,
      recordingFileName,
      consent,
      posterId,
      topic,
      authorFullName,
      authorEmail,
      authorFacebook,
      authorTwitter,
      authorLn,
      fullName,
      email,
      phone,
      gdprConsent,
      postersConsent,
    } = formState;

    let recaptchaCode = recaptchaRef.current.getValue();

    data.append("title", title);
    data.append("authors", authors);
    data.append("fileName", fileName.replaceAll(" ", ""));
    data.append("recordingFileName", recordingFileName.replaceAll(" ", ""));
    data.append("file", file);
    data.append("recordingFile", recordingFile);
    data.append("consent", consent);
    data.append("posterId", posterId);
    data.append("topic", topic);
    data.append("authorFullName", authorFullName);
    data.append("authorEmail", authorEmail);
    data.append("authorFacebook", authorFacebook);
    data.append("authorTwitter", authorTwitter);
    data.append("authorLn", authorLn);
    data.append("fullName", fullName);
    data.append("email", email);
    data.append("phone", phone);
    data.append("gdprConsent", gdprConsent);
    data.append("postersConsent", postersConsent);
    data.append("recaptcha", recaptchaCode);

    setFormState((f) => ({
      ...f,
      isSending: true,
    }));

    const response = await fetch(`${API_URL}/new-application/v2?conf=${CONF}`, {
      method: "POST",
      body: data,
    });

    if (response.status === 201) {
      setFormState((f) => ({
        ...f,
        isSending: false,
        sent: true,
      }));
      client.emit("unsubscribe-from-poster", formState.posterId);
    } else if (response.status === 403) {
      const responseText = await response.text();
      recaptchaRef.current.reset();
      if (responseText !== "Forbidden") {
        setFormValidationMessage(
          `Błąd przesyłania formularza: ${responseText}`
        );
      }
      setFormState((f) => ({
        ...f,
        isSending: false,
        failure: true,
      }));
    } else {
      recaptchaRef.current.reset();
      setFormState((f) => ({
        ...formState,
        isSending: false,
        failure: true,
      }));
    }
  };

  const handleTextInputChange = (event) => {
    event.persist();

    setFormState((f) => ({
      ...f,
      [event.target.name]: event.target.value,
    }));
  };

  const handleFileChange = (event) => {
    event.persist();
    if (
      ![
        "application/pdf",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "image/jpg",
        "image/jpeg",
      ].includes(event.target.files[0].type)
    ) {
      setFormState((f) => ({
        ...f,
        fileInvalid: true,
        validationMessage: "Obsługiwane typy plików to: jpeg, pptx, ppt i pdf.",
      }));
      return;
    }

    if (event.target.files[0].size > 5e7) {
      setFormState((f) => ({
        ...f,
        fileInvalid: true,
        validationMessage: "Maksymalny rozmiar pliku to 10MB.",
      }));
      return;
    }

    setFormState((f) => ({
      ...f,
      fileInvalid: false,
      file: event.target.files[0],
      fileName: event.target.files[0].name,
    }));
  };

  useEffect(() => {
    const loadData = async () => {
      const posterDataResponse = await fetch(
        `${API_URL}/new-application/${formState.posterId}?conf=${CONF}`,
        {}
      );

      if (posterDataResponse.status === 200) {
        const posterData = await posterDataResponse.json();
        setFormState((f) => ({
          ...initialFormState,
          ...posterData,
          posterId: posterData.code,
          posterIdInvalid: false,
        }));
        client.emit("subscribe-to-poster", formState.posterId);
      } else if (posterDataResponse.status === 404) {
        setFormState((f) => ({
          ...initialFormState,
          posterId: f.posterId,
          posterIdInvalid: true,
          posterIdValidationMessage: "NOT_FOUND",
        }));
      } else if (posterDataResponse.status === 409) {
        setFormState((f) => ({
          ...initialFormState,
          posterId: f.posterId,
          posterIdInvalid: true,
          posterIdValidationMessage: "ALREADY_EXISTS",
        }));
      } else {
        setFormState((f) => ({
          ...initialFormState,
          posterId: f.posterId,
          posterIdInvalid: true,
          posterIdValidationMessage: "Wprowadzono nieprawidłowy kod",
        }));
      }
    };

    if (formState.posterId.length === 4) {
      loadData();
    }
  }, [formState.posterId]);

  const getPosterValidationMessage = (language) => {
    if (language === "PL") {
      switch (formState.posterIdValidationMessage) {
        case "ALREADY_EXISTS":
          return "Plakat o takim numerze już został umieszczony na Platformie. Chcesz zmienić plakat – skontaktuj się z Biurem Organizacyjnym – otrzymasz nowy kod.";
        case "NOT_FOUND":
          return "Plakat o podanym kodzie nie istnieje.";
        default:
          return "Wprowadzono nieprawidłowy kod.";
      }
    } else {
      switch (formState.posterIdValidationMessage) {
        case "ALREADY_EXISTS":
          return "A poster with this number has already been placed on the Platform. If you want to change the poster - contact the Organizing Office - you will receive a new code.";
        case "NOT_FOUND":
          return "The poster with the given code does not exist.";
        default:
          return "Incorrect code entered.";
      }
    }
  };

  return (
    <LanguageContext.Consumer>
      {({ language }) => (
        <div className="new-application-container ">
          <Form onSubmit={handleSubmit}>
            <h4>
                 1. Wprowadź kod plakatu* / Provide poster code*
            </h4>
            <Form.Group controlId="posterId">
              <Form.Control
                isInvalid={formState.posterIdInvalid}
                isValid={
                  !formState.posterIdInvalid && formState.title.length > 0
                }
                name="posterId"
                onChange={handleTextInputChange}
                value={formState.posterId}
                required
                type="text"
                placeholder={language === "PL" ? "Kod plakatu" : "Poster code"}
              />
              <Form.Control.Feedback
                style={{
                  display: formState.posterIdInvalid ? "block" : "none",
                }}
                type="invalid"
              >
                {getPosterValidationMessage(language)}
              </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Podaj kod plakatu widoczny na <a
                    href="https://immunologia2024.pl/plakaty/#PostersList"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    liście prac zakwalifikowanych do prezentacji plakatowej
                  </a> / Provide the poster code in accordance with <a
                    href="https://immunologia2024.pl/plakaty/#PostersList"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                     the table
                  </a> in the
                  notice of accepted abstracts.{" "}
                  {/* <a
                    href="https://zjazd.ptchp.org/streszczenia/lista-zakwalifikowanych-abstraktow/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://zjazd.ptchp.org/streszczenia/lista-zakwalifikowanych-abstraktow/
                  </a> */}
                  .
                </Form.Text>
            </Form.Group>
              <h4>
                2. Sprawdź dane plakatu - jeżeli się nie zgadzają lub chcesz je
                poprawić skontaktuj się z{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="mailto:konferencje@altasoft.pl"
                >
                  Biurem Organizacyjnym
                </a> / 
                Check the poster data - if they do not match or you want them
                to improve contact{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="mailto:konferencje@altasoft.pl"
                >
                  the organizational office
                </a>
                :
              </h4>
            <Form.Group controlId="title">
              <Form.Label> Tytuł / Title*</Form.Label>
              <Form.Control
                as="textarea"
                rows={Math.floor(formState.title.length / 90) + 1}
                name="title"
                value={formState.title}
                disabled
                type="text"
                placeholder={
                  language === "PL"
                    ? "np. Rozszerzone badania nad najnowszym odkryciem w dziedzinie medycyny"
                    : "ie: Extended research on the latest discovery"
                }
              />
            </Form.Group>
            <Form.Group controlId="authors">
              <Form.Label>
               Autorzy / Authors*
              </Form.Label>
              <Form.Control
                name="authors"
                as="textarea"
                rows={Math.floor(formState.authors.length / 90) + 1}
                value={formState.authors}
                disabled
                type="text"
                placeholder="np. Grzegorz Autorowski, Paulina Autorska, Barbara Autorowicz"
              />
            </Form.Group>
            <Form.Group controlId="topic">
              <Form.Label>Temat" / "Topic"*</Form.Label>
              <Form.Control
                name="topic"
                value={formState.topic}
                disabled
                type="text"
              />
            </Form.Group>
            <Form.Group controlId="session">
              <Form.Label>
               Sesja / Session*
              </Form.Label>
              <Form.Control
                name="session"
                value={formState.session}
                disabled
                type="text"
              />
            </Form.Group>
            <h4>3. Wybierz pliki / Upload files</h4>

            <Form.Group controlId="presentationFile">
              <Form.Label>
                Plik prezentacji / Presentation file*
              </Form.Label>
              <Form.File
                isInvalid={formState.fileInvalid}
                required
                onChange={handleFileChange}
                id="custom-file"
                label={`${formState.fileName || "np. Prezentacja.pptx"}`}
                custom
                accept=".jpeg,.pptx,.ppt,.pdf"
              />
              <Form.Text className="text-muted">
                  Obsługiwane typy plików to: jpeg, pptx, ppt i pdf. Uwaga! Plakat nie powinien mieć więcej niż 4 strony.
                  / Supported file types: jpeg, pptx, ppt and pdf.
              </Form.Text>
              <Form.Control.Feedback
                style={{
                  display: formState.fileInvalid ? "block" : "none",
                }}
                type="invalid"
              >
                {language === "PL"
                  ? formState.validationMessage
                  : "Supported file types: jpeg, pptx, ppt and pdf. The maximum file size is 10 MB."}
              </Form.Control.Feedback>
            </Form.Group>
            <h4>
                4. Podaj dane kontaktowe autora, z którym mogą kontaktować się osoby zainteresowane przedstawioną pracą.
                / Provide the author's contact details, who can be contacted by people interested in the submitted paper.
            </h4>
            <Form.Group controlId="authorFullName">
              <Form.Label>
                  Tytuł, imię, nazwisko /
                  Title, name, surname*
              </Form.Label>
              <Form.Control
                name="authorFullName"
                onChange={handleTextInputChange}
                value={formState.authorFullName}
                type="text"
                required
                placeholder={
                  language === "PL"
                    ? "np. dr n. med. Łukasz Koziński"
                    : "ie. prof. Josh Smith"
                }
              />
            </Form.Group>
            <Form.Group controlId="authorEmail">
              <Form.Label>
                Adres email / E-mail address*
              </Form.Label>
              <Form.Control
                name="authorEmail"
                onChange={handleTextInputChange}
                value={formState.authorEmail}
                required
                type="email"
                placeholder={`${
                  language === "PL" ? "np." : "ie."
                } michal.polonczyk@gmail.com`}
              />
            </Form.Group>
            <Form.Group controlId="authorFacebook">
              <Form.Label>Facebook</Form.Label>
              <Form.Control
                name="authorFacebook"
                onChange={handleTextInputChange}
                value={formState.authorFacebook}
                type="text"
                placeholder={`${
                  language === "PL" ? "np." : "ie."
                } facebook.com/michal.lukasz111`}
              />
            </Form.Group>
            <Form.Group controlId="authorTwitter">
              <Form.Label>Twitter</Form.Label>
              <Form.Control
                name="authorTwitter"
                onChange={handleTextInputChange}
                value={formState.authorTwitter}
                type="text"
                placeholder={`${
                  language === "PL" ? "np." : "ie."
                }  @michal.lukasz111`}
              />
            </Form.Group>
            <Form.Group controlId="authorLn">
              <Form.Label>LinkedIn</Form.Label>
              <Form.Control
                name="authorLn"
                onChange={handleTextInputChange}
                value={formState.authorLn}
                type="text"
                placeholder={`${
                  language === "PL" ? "np." : "ie."
                }  michal-lukasz-112j11a`}
              />
            </Form.Group>

            <div className="form-submit">
              <div
                style={{
                  display:
                    formState.isSending || formState.sent ? "none" : "block",
                }}
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Ld6QMMaAAAAAIAY9tDFWw7B_m_LNlSFENwp7gnz"
                />
              </div>
              {formState.sent ? (
                <Alert variant="success">
                  <Alert.Heading>
                    {language === "PL"
                      ? "Zgłoszenie zostało przyjęte."
                      : "The application has been accepted."}
                  </Alert.Heading>
                </Alert>
              ) : (
                <Button
                  disabled={
                    formState.posterIdInvalid ||
                    formState.isSending ||
                    recaptchaRef.current?.getValue() === null
                  }
                  variant="primary"
                  type="submit"
                >
                  {formState.isSending ? (
                    <>
                      <ProgressBar
                        animated
                        now={progress}
                        label={`${progress}%`}
                      />
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      {language === "PL" ? "Wysyłanie..." : "Sending..."}
                    </>
                  ) : language === "PL" ? (
                    "Wyślij"
                  ) : (
                    "Submit"
                  )}
                </Button>
              )}

              <Form.Control.Feedback
                style={{
                  display: formState.failure ? "block" : "none",
                  textAlign: "right",
                  fontSize: "1rem",
                  fontWeight: "500",
                }}
                type="invalid"
              >
                {language === "PL"
                  ? formValidationMessage
                  : "Error sending the form. Make sure the details are entered correct and try again. If the error persists, contact the Organizational Office."}
              </Form.Control.Feedback>
            </div>
          </Form>
        </div>
      )}
    </LanguageContext.Consumer>
  );
};

export default NewApplication;
